import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ClickableDiv} from '@Components/clickable-div';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {ActionMenu} from '@Components/action-menu/action-menu';
import {noop} from '@Utils/general.util';
import type {ActionMenuFlyoutProps} from './action-menu-flyout.types';
import {FLYOUT_HOVER_INTENT_MS, FLYOUT_TOP_ALIGN_OFFSET} from './action-menu-flyout.types';
import styles from './action-menu-flyout.module.scss';

export function ActionMenuFlyout({items, triggerText, triggerIcon, infoNote, isDisabled, onOpen}: ActionMenuFlyoutProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const commitOpenState = (open: boolean): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (open && !isOpenRef.current) {
      onOpen?.();
    }
    isOpenRef.current = open;
    setIsOpen(open);
  };

  const scheduleOpen = (open: boolean): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout((): void => {
      commitOpenState(open);
    }, FLYOUT_HOVER_INTENT_MS);
  };

  const openFlyout = (): void => {
    if (isDisabled) {
      return;
    }
    commitOpenState(true);
  };

  const closeFlyout = (): void => {
    commitOpenState(false);
  };

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Dropdown
      open={isOpen}
      isControlled
      stopClickPropagation
      doCloseOnInnerClick={false}
      onOpen={openFlyout}
      onClose={closeFlyout}
      position={DROPDOWN_POSITION.FIXED}
      alignment={ALIGNMENT_TYPE.RIGHT_START}
      topOffset={FLYOUT_TOP_ALIGN_OFFSET}
      popupClassName={styles.flyoutPopup}
      selector={
        <ClickableDiv
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={noop}
          onMouseEnter={(): void => {
            scheduleOpen(true);
          }}
          onMouseLeave={(): void => {
            scheduleOpen(false);
          }}
        >
          <ControlledListItemType7 id="action-menu-trigger" icon={triggerIcon} text={triggerText} rightIcon="icon-caret-right" className="radius-8" isDisabled={isDisabled} />
        </ClickableDiv>
      }
      popup={
        <div
          onMouseEnter={(): void => {
            scheduleOpen(true);
          }}
          onMouseLeave={(): void => {
            scheduleOpen(false);
          }}
        >
          <ActionMenu isMobile={false} items={items} infoNote={infoNote} />
        </div>
      }
    />
  );
}
